/**
 * PIN Setup Modal
 * 
 * Allows users to set up a PIN for document access
 */

import { useState, useEffect } from 'react';
import { View, Modal, Pressable, TextInput, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Button } from '@/components/ui/Button';
import { ThemedText, SubHeading, Caption } from '@/components/ui/Text';
import { hasPIN, setPIN } from '@/services/biometricAuth';

type PINSetupModalProps = {
  visible: boolean;
  onComplete: () => void;
  onCancel: () => void;
};

export function PINSetupModal({ visible, onComplete, onCancel }: PINSetupModalProps) {
  const theme = useTheme();
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [step, setStep] = useState<'create' | 'confirm'>('create');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isChanging, setIsChanging] = useState(false);

  // Check if PIN already exists when modal opens
  useEffect(() => {
    let isActive = true;

    const checkExistingPIN = async () => {
      try {
        if (!visible) return;
        const hasPin = await hasPIN();
        if (isActive) {
          setIsChanging(hasPin);
        }
      } catch (err) {
        console.error('[PINSetupModal] Error checking PIN:', err);
        if (isActive) {
          setIsChanging(false);
        }
      }
    };

    checkExistingPIN();

    return () => {
      isActive = false;
    };
  }, [visible]);

  const handleCreatePIN = () => {
    if (pin.length < 4) {
      setError('PIN must be at least 4 digits');
      return;
    }
    setError('');
    setStep('confirm');
    setConfirmPin('');
  };

  const handleConfirmPIN = async () => {
    if (confirmPin.length < 4) {
      setError('Please enter the full PIN');
      return;
    }

    if (pin !== confirmPin) {
      setError('PINs do not match. Please try again.');
      setConfirmPin('');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      const success = await setPIN(pin);
      
      if (success) {
        Alert.alert(
          isChanging ? 'PIN Changed Successfully' : 'PIN Set Successfully',
          isChanging
            ? 'Your PIN has been updated. You can use it to access your documents.'
            : 'Your PIN has been saved. You can use it to access your documents.',
          [{ text: 'OK', onPress: onComplete }]
        );
        // Reset state
        setPin('');
        setConfirmPin('');
        setStep('create');
      } else {
        setError('Failed to save PIN. Please try again.');
      }
    } catch (error) {
      console.error('[PINSetupModal] Error setting PIN:', error);
      setError('Failed to save PIN. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setPin('');
    setConfirmPin('');
    setStep('create');
    setError('');
    onCancel();
  };

  return (
    <Modal
      visible={visible}
      animationType="fade"
      transparent
      onRequestClose={handleCancel}
    >
      <View
        style={{
          flex: 1,
          backgroundColor: theme.colors.modalOverlay,
          justifyContent: 'center',
          alignItems: 'center',
          padding: theme.spacing(4),
        }}
      >
        <View
          style={{
            backgroundColor: theme.colors.surface,
            borderRadius: theme.radii.xl,
            width: '100%',
            maxWidth: 400,
            overflow: 'hidden',
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: theme.spacing(2),
              borderBottomWidth: 1,
              borderBottomColor: theme.colors.borderMuted,
            }}
          >
            <SubHeading>Set Up PIN</SubHeading>
            <Pressable onPress={handleCancel}>
              <Ionicons name="close" size={24} color={theme.colors.text} />
            </Pressable>
          </View>

          <View style={{ padding: theme.spacing(4) }}>
            <View style={{ alignItems: 'center', marginBottom: theme.spacing(3) }}>
              <View
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: 40,
                  backgroundColor: theme.colors.primary + '15',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: theme.spacing(2),
                }}
              >
                <Ionicons name="lock-closed" size={40} color={theme.colors.primary} />
              </View>
              <SubHeading style={{ marginBottom: theme.spacing(1), textAlign: 'center' }}>
                {step === 'create' ? (isChanging ? 'Change PIN' : 'Create PIN') : 'Confirm PIN'}
              </SubHeading>
              <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
                {step === 'create'
                  ? isChanging
                    ? 'Enter a new 4-6 digit PIN'
                    : 'Enter a 4-6 digit PIN to secure your documents'
                  : 'Re-enter your PIN to confirm'}
              </Caption>
            </View>

            <View style={{ marginBottom: theme.spacing(2) }}>
              <TextInput
                value={step === 'create' ? pin : confirmPin}
                onChangeText={(text) => {
                  const numericText = text.replace(/[^0-9]/g, '');
                  if (step === 'create') {
                    setPin(numericText);
                  } else {
                    setConfirmPin(numericText);
                  }
                  setError('');
                }}
                placeholder={step === 'create' ? 'Enter PIN' : 'Confirm PIN'}
                placeholderTextColor={theme.colors.textSecondary}
                secureTextEntry
                keyboardType="number-pad"
                maxLength={6}
                style={{
                  borderWidth: 1,
                  borderColor: error ? theme.colors.error : theme.colors.borderMuted,
                  borderRadius: theme.radii.md,
                  padding: theme.spacing(2),
                  fontSize: 18,
                  textAlign: 'center',
                  letterSpacing: 8,
                  color: theme.colors.text,
                  backgroundColor: theme.colors.background,
                }}
                autoFocus
              />
              {error ? (
                <Caption color={theme.colors.error} style={{ marginTop: theme.spacing(1), textAlign: 'center' }}>
                  {error}
                </Caption>
              ) : null}
            </View>

            <View style={{ gap: theme.spacing(2) }}>
              {step === 'create' ? (
                <Button
                  label="Continue"
                  onPress={handleCreatePIN}
                  disabled={pin.length < 4}
                />
              ) : (
                <Button
                  label="Confirm PIN"
                  onPress={handleConfirmPIN}
                  disabled={confirmPin.length < 4 || isSaving}
                  loading={isSaving}
                />
              )}
              <Button
                label="Cancel"
                variant="secondary"
                onPress={handleCancel}
                disabled={isSaving}
              />
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
}

